// LivestreamJoinView.js
// Public page at /join — stream viewers AND in-person guests (booth banner QR)
// order without a physical ticket. Front screen is a three-way tier picker:
//   ⚡ Flash/freestyle $10 · 📷 Own photo $30 · 🕐 Standby free
// All tiers create the order (standby lane) via livestreamJoin first; paid
// tiers then redirect straight into Square checkout, so an abandoned payment
// degrades into a normal standby order. Standby lands on /order?t={tokenId}.
//
// PARTY VOICE (shift.qrFlows.mode === "free" — see utils/qrFlow.js): at a
// client-paid private event the guest is a PARTY GUEST, not a customer. The
// tier picker is skipped entirely — one "hop in line" form, no prices, no
// "free/order/standby" words, and structurally NO checkout branch (the free
// flow can never reach a charge path from this page). The server derives the
// lane from the voice: free-voice joins enter the MAIN queue with a number at
// join (nothing to pay = nothing to hold on standby); paid-voice joins ride
// standby until Square confirms payment. The lane is never client-declared.

import { useState, useEffect, useRef } from "react";
import { db, auth, storage } from "./firebase";
import { signInAnonymously, onAuthStateChanged } from "firebase/auth";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { collection, query, where, getDocs } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import useColorPalette from "./hooks/useColorPalette";
import useFlashDesigns from "./hooks/useFlashDesigns";
import FramingTool from "./FramingTool";
import ShiftPicker from "./components/ShiftPicker";
import { guestSafeLabel, resolveShift, setBoundShift, shiftLabel } from "./utils/shiftScope";
import { COMBOS, COMBO_ORDER, fmtCents } from "./utils/combos";
import {
  flashPriceCents, customPriceCents, comboPriceCents, comboAlaCarteCents,
  activeExtras, hideStandbyTier,
} from "./utils/eventPricing";

// Anonymous per-browser id — lets the server stamp deviceRequestSeq (the
// "3rd request from this device" signal on the queue cards). Random UUID in
// localStorage, no fingerprinting, no PII. Signal only, never enforcement.
function getDeviceId() {
  try {
    let id = localStorage.getItem("gts_device_id");
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem("gts_device_id", id);
    }
    return id;
  } catch {
    return null; // storage blocked — join proceeds without the signal
  }
}

const BRAND = {
  navy:   "#1d52a0",
  gold:   "#fcb31f",
  sky:    "#4aa4e1",
  pink:   "#ef537e",
  cream:  "#ffdaad",
  dark:   "#0d1833",
};

export default function LivestreamJoinView() {
  const COLOR_OPTIONS = useColorPalette();

  const [shift, setShift]               = useState(undefined); // undefined=loading, null=closed, object=open
  const { designs, freestyleDesign, allDesigns, loading: designsLoading } = useFlashDesigns(shift || null);
  // Why /join is closed when shift is null. Used to render an accurate
  // "closed" screen instead of one generic message.
  //   "no_shift"             — no active shift in Firestore
  //   "queue_closed"         — shift exists but isQueueOpen is false
  //   "stream_join_disabled" — shift active and queue open, but livestreamJoinEnabled is false
  const [closedReason, setClosedReason] = useState(null);
  // Ordering tier — null shows the front tier-picker screen.
  //   "flash"   — pick a design / freestyle, $10, straight to checkout
  //   "custom"  — upload a photo, $30, straight to checkout
  //   "standby" — free, keeps the design/photo toggle, lands on the portal
  const [tier, setTier]                 = useState(null);
  // Combo interstitial (custom tier, market voice only) — null shows the
  // combo screen; "none" = just the pancake; "artist"/"collector" ride to
  // createLivestreamCheckout as a PRODUCT choice (server prices it).
  const [comboChoice, setComboChoice]   = useState(null);
  // Road-mode extras (add-on KEYS only — "toppings", "signed_print"): priced
  // by the shift's pricing block, charged server-side, never sent as amounts.
  const [extraChoices, setExtraChoices] = useState([]);
  const [name, setName]                 = useState("");
  const [email, setEmail]               = useState("");
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [selectedColors, setSelectedColors] = useState([]);
  const [freestyleText, setFreestyleText]   = useState("");
  const [subjectName, setSubjectName]       = useState("");
  const [photoNotes, setPhotoNotes]         = useState("");
  const [submitting, setSubmitting]     = useState(false);
  const [error, setError]               = useState(null);
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);

  // Photo upload
  const [mode, setMode]                   = useState("design"); // "design" | "photo"
  const [photoFile, setPhotoFile]         = useState(null);
  const [croppedBlob, setCroppedBlob]     = useState(null);
  const [framingDone, setFramingDone]     = useState(false);
  const [authed, setAuthed]               = useState(false);
  const fileInputRef                      = useRef(null);

  // Anonymous auth for Storage uploads. Wait for the persisted session to
  // restore before going anonymous — signInAnonymously at mount races the
  // restore and replaces a signed-in staff session for the whole browser.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u || auth.currentUser) { setAuthed(true); return; }
      signInAnonymously(auth).catch(() => {});
    });
    return unsub;
  }, []);

  // Which booth is this guest at? (2026-09-25)
  //
  // Was limit(1) → docs[0], fine while exactly one event could be live. With
  // two booths running, that silently dropped guests into the other venue's
  // queue — they'd hold a number that means nothing where they're standing.
  //
  // Two arrival paths, and they need different answers:
  //   screen QR  → /join?s=KXQD, scoped, no prompt, no extra tap
  //   banner QR  → linktree → bare /join. Ambiguous ONLY when 2+ booths are
  //                live, and then we ask rather than guess.
  //
  // resolveShift short-circuits to the single active shift when there is one,
  // so the bare-/join path is untouched on an ordinary day — the picker
  // cannot appear at a solo market.
  const [boothChoices, setBoothChoices] = useState([]);

  useEffect(() => {
    let cancelled = false;

    function gate(s) {
      if (!s.isQueueOpen)           { setClosedReason("queue_closed");         setShift(null); return; }
      if (!s.livestreamJoinEnabled) { setClosedReason("stream_join_disabled"); setShift(null); return; }
      setClosedReason(null);
      setShift(s);
    }

    async function check() {
      try {
        const snap = await getDocs(query(collection(db, "shifts"), where("isActive", "==", true)));
        if (cancelled) return;
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        if (all.length === 0) { setClosedReason("no_shift"); setShift(null); return; }

        const { shift: resolved, needsChoice, candidates } = resolveShift(all);
        if (needsChoice) {
          // Only offer booths a guest could actually join — a closed queue in
          // the list is a dead end that reads as our mistake, not theirs.
          const joinable = candidates.filter(s => s.isQueueOpen && s.livestreamJoinEnabled);
          if (joinable.length === 0) { setClosedReason("queue_closed"); setShift(null); return; }
          if (joinable.length === 1) { setBoothChoices([]); gate(joinable[0]); return; }
          setBoothChoices(joinable);
          setShift(null);
          setClosedReason(null);
          return;
        }
        setBoothChoices([]);
        gate(resolved);
      } catch { setClosedReason("no_shift"); setShift(null); }
    }
    check();
    return () => { cancelled = true; };
  }, []);

  function pickTier(t) {
    setTier(t);
    setError(null);
    setComboChoice(null);
    setExtraChoices([]);
    if (t === "custom") {
      setMode("photo");
      setSelectedDesign(null);
      setFreestyleText("");
    } else {
      setMode("design");
      setPhotoFile(null);
      setCroppedBlob(null);
      setFramingDone(false);
    }
  }

  async function handleSubmit() {
    if (!name.trim() || submitting) return;
    if (mode === "design" && !selectedDesign) return;
    if (mode === "photo" && !croppedBlob) return;
    setSubmitting(true);
    setError(null);

    try {
      // Upload photo if custom mode
      let imageUrl = null;
      let croppedImageUrl = null;
      if (mode === "photo" && croppedBlob) {
        // Ensure anonymous auth is ready for Storage upload
        if (!authed) {
          try { await signInAnonymously(auth); setAuthed(true); } catch (e) {
            throw new Error("Could not authenticate for photo upload. Try again.");
          }
        }
        const ts = Date.now();
        const path = `livestream-uploads/${ts}_${Math.random().toString(36).slice(2, 8)}.jpg`;
        const ref = storageRef(storage, path);
        await uploadBytes(ref, croppedBlob, { contentType: "image/jpeg" });
        croppedImageUrl = await getDownloadURL(ref);
        imageUrl = croppedImageUrl;
        if (!croppedImageUrl) throw new Error("Photo upload failed — no URL returned.");
      }

      const isCustomPhoto = mode === "photo" && croppedImageUrl;

      // Create the order
      const joinFn = httpsCallable(getFunctions(getApp()), "livestreamJoin");
      const result = await joinFn({
        name:          name.trim(),
        email:         email.trim() || null,
        designId:      isCustomPhoto ? null : selectedDesign?.id,
        designLabel:   isCustomPhoto ? null : selectedDesign?.label,
        isFreestyle:   isCustomPhoto ? false : (selectedDesign?.isFreestyle || false),
        orderType:     isCustomPhoto ? "custom" : undefined,
        imageUrl:      isCustomPhoto ? imageUrl : undefined,
        croppedImageUrl: isCustomPhoto ? croppedImageUrl : undefined,
        favoriteColors: selectedColors.length > 0 ? selectedColors : null,
        freestyleText: !isCustomPhoto && selectedDesign?.isFreestyle ? freestyleText.trim() : null,
        subjectName:   isCustomPhoto ? (subjectName.trim() || null) : null,
        notes:         isCustomPhoto ? (photoNotes.trim() || null) : null,
        shiftId:       shift.id,
        deviceId:      getDeviceId(),
        // Lane is server-derived from the shift's voice — see header comment.
      });

      const { tokenId, queueNumber, orderId } = result.data;

      // Store in localStorage so CustomerOrderView picks it up
      try {
        localStorage.setItem(`gts_order_${tokenId}`, JSON.stringify({
          orderId, queueNumber, ts: Date.now(),
        }));
      } catch {}

      // Paid tiers: straight into Square checkout. The order already exists on
      // standby, so if checkout creation fails (or the guest abandons payment)
      // they degrade gracefully into a normal standby order on the portal.
      if (tier === "flash" || tier === "custom") {
        try {
          const checkoutFn = httpsCallable(getFunctions(getApp()), "createLivestreamCheckout");
          const co = await checkoutFn({
            orderId, tokenId, customerName: name.trim(),
            // Product choice only — the server prices combos from its own table.
            comboKey: tier === "custom" && COMBOS[comboChoice] ? comboChoice : undefined,
            // Road-mode extras — keys only, validated + priced server-side.
            // Filtered against what's actually on offer so a combo that
            // already includes an add-on can't double-sell it.
            extras: (() => {
              const keys = extraChoices.filter((k) => extrasOnOffer.some((e) => e.key === k));
              return keys.length ? keys : undefined;
            })(),
          });
          if (co.data?.checkoutUrl) {
            window.location.href = co.data.checkoutUrl;
            return;
          }
        } catch (coErr) {
          console.error("Checkout creation failed, falling back to standby portal:", coErr);
        }
      }

      // Standby (or checkout fallback): portal — upgrade option lives there
      window.location.href = `/order?t=${tokenId}`;

    } catch (err) {
      console.error("Join failed:", err);
      const msg = err.message?.includes("Please wait") ? err.message
        : err.message?.includes("not enabled") ? "Livestream join isn't open right now."
        : err.message?.includes("closed") ? "The queue just closed — try again next stream!"
        : err.message || "Something went wrong. Try again.";
      setError(msg);
      setSubmitting(false);
    }
  }

  // ── LOADING ──
  // Don't block on designsLoading — hook initializes with hardcoded fallback synchronously.
  // The onSnapshot may hang for unauthenticated users.
  if (shift === undefined) {
    return (
      <div style={{ minHeight: "100vh", background: BRAND.navy, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontFamily: "var(--jop-display)" }}>
        Loading...
      </div>
    );
  }

  // ── WHICH BOOTH? ──
  // Only when two or more joinable booths are live and the link carried no
  // scope (the banner → linktree → bare /join path). Guests pick by event
  // name or type the code off the booth screen. The pick is remembered on
  // their phone so a second scan (order a friend a pancake, re-open the tab)
  // doesn't ask again; it self-heals once that shift closes.
  if (boothChoices.length > 1) {
    return (
      <ShiftPicker
        candidates={boothChoices}
        audience="guest"
        onPick={(s) => {
          setBoundShift(s.id, shiftLabel(s));
          setBoothChoices([]);
          setShift(s);
          setClosedReason(null);
        }}
      />
    );
  }

  // ── CLOSED ──
  if (!shift) {
    const closedCopy = {
      no_shift: {
        headline: "The griddle is closed right now",
        body: <>Catch us at the next event or stream — see the schedule at <span style={{ color: BRAND.sky }}>thejoyofpancakes.com</span></>,
      },
      queue_closed: {
        headline: "Today's queue is full",
        body: <>We've stopped taking new orders for this session.<br />Thanks for stopping by!</>,
      },
      stream_join_disabled: {
        headline: "Online ordering paused",
        body: <>We're cooking right now, but online orders aren't open for this session.<br />Come say hi at the booth, or find the next event at <span style={{ color: BRAND.sky }}>thejoyofpancakes.com</span></>,
      },
    };
    const c = closedCopy[closedReason] || closedCopy.no_shift;
    return (
      <div style={{ minHeight: "100vh", background: BRAND.navy, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2rem", fontFamily: "var(--jop-display)", textAlign: "center", gap: "1rem" }}>
        <img src="/jop_logo_wide.png" alt="The Joy of Pancakes" style={{ height: "120px" }} />
        <div style={{ color: BRAND.gold, fontSize: "1.4rem", fontWeight: "bold" }}>{c.headline}</div>
        <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.95rem", lineHeight: 1.6, maxWidth: "360px" }}>
          {c.body}
        </div>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: "0.5rem",
            background: "rgba(74,164,225,0.15)",
            border: `1px solid ${BRAND.sky}`,
            color: BRAND.sky,
            borderRadius: "999px",
            padding: "0.6rem 1.4rem",
            fontSize: "0.9rem",
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          ↻ Refresh to check again
        </button>
      </div>
    );
  }

  // Party voice — client-paid event: no tiers, no prices, no checkout.
  const partyVoice = shift?.qrFlows?.mode === "free";
  const effectiveTier = tier || (partyVoice ? "standby" : null);

  // Derived prices — Road Mode (shift.pricing) overrides the base table for
  // this shift only; absent block renders the classic $10/$30.
  const flashPrice  = fmtCents(flashPriceCents(shift));
  const customPrice = fmtCents(customPriceCents(shift));
  const standbyHidden = hideStandbyTier(shift);

  // ── TIER PICKER (front screen) — skipped entirely in party voice ──
  if (!effectiveTier) {
    return (
      <div style={{ minHeight: "100vh", background: BRAND.navy, fontFamily: "var(--jop-display)", color: "white" }}>
        <div role="main" style={{ maxWidth: "480px", margin: "0 auto", padding: "1.5rem 1.25rem" }}>

          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
            <img src="/jop_logo_wide.png" alt="The Joy of Pancakes" style={{ height: "96px", marginBottom: "0.5rem" }} />
            <div style={{ color: BRAND.gold, fontSize: "1.3rem", fontWeight: "bold" }}>Order a Pancake</div>
            {guestSafeLabel(shift) && <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", marginTop: "0.25rem" }}>{guestSafeLabel(shift)}</div>}
            <div style={{ color: BRAND.sky, fontSize: "0.8rem", marginTop: "0.25rem" }}>Custom pancake art, drawn fresh at the griddle</div>
          </div>

          {/* Paid tiers */}
          <button onClick={() => pickTier("flash")} style={{
            width: "100%", boxSizing: "border-box", textAlign: "left", cursor: "pointer",
            background: "rgba(252,179,31,0.12)", border: "2px solid rgba(252,179,31,0.6)",
            borderRadius: "14px", padding: "1rem 1.1rem", marginBottom: "0.75rem",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "0.5rem" }}>
              <span style={{ color: BRAND.gold, fontSize: "1.05rem", fontWeight: "bold", fontFamily: "var(--jop-display)" }}>⚡ Flash or Freestyle</span>
              <span style={{ color: BRAND.gold, fontSize: "1.15rem", fontWeight: "bold", fontFamily: "var(--jop-display)", whiteSpace: "nowrap" }}>{flashPrice}</span>
            </div>
            <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "0.82rem", lineHeight: 1.5, marginTop: "0.3rem", fontFamily: "var(--jop-display)" }}>
              Pick one of today's designs — or tell us what to draw. Guaranteed spot in the queue.
            </div>
          </button>

          <button onClick={() => pickTier("custom")} style={{
            width: "100%", boxSizing: "border-box", textAlign: "left", cursor: "pointer",
            background: "rgba(74,164,225,0.12)", border: "2px solid rgba(74,164,225,0.6)",
            borderRadius: "14px", padding: "1rem 1.1rem", marginBottom: "1.25rem",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "0.5rem" }}>
              <span style={{ color: BRAND.sky, fontSize: "1.05rem", fontWeight: "bold", fontFamily: "var(--jop-display)" }}>📷 Your Own Photo</span>
              <span style={{ color: BRAND.sky, fontSize: "1.15rem", fontWeight: "bold", fontFamily: "var(--jop-display)", whiteSpace: "nowrap" }}>{customPrice}</span>
            </div>
            <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "0.82rem", lineHeight: 1.5, marginTop: "0.3rem", fontFamily: "var(--jop-display)" }}>
              Upload a photo and we'll turn it into pancake art. Guaranteed spot in the queue.
            </div>
          </button>

          {/* Standby — deliberately quieter than the paid tiers. Road Mode can
              hide it (solo events: every slot paid; comps ride the register QR). */}
          {!standbyHidden && (
          <button onClick={() => pickTier("standby")} style={{
            width: "100%", boxSizing: "border-box", textAlign: "left", cursor: "pointer",
            background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: "12px", padding: "0.8rem 1.1rem",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "0.5rem" }}>
              <span style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.92rem", fontWeight: "bold", fontFamily: "var(--jop-display)" }}>🕐 Standby</span>
              <span style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.92rem", fontWeight: "bold", fontFamily: "var(--jop-display)", whiteSpace: "nowrap" }}>Free</span>
            </div>
            <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.78rem", lineHeight: 1.5, marginTop: "0.25rem", fontFamily: "var(--jop-display)" }}>
              We get to standby requests when there's breathing room — no promises. Upgrade to a guaranteed spot anytime (⚡ {flashPrice} / 📷 {customPrice}).
            </div>
          </button>
          )}

          <div style={{ textAlign: "center", marginTop: "1.5rem", color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
            thejoyofpancakes.com
          </div>
        </div>
      </div>
    );
  }

  // ── COMBO INTERSTITIAL (custom tier, market voice only) ──
  // One decision, right after picking 📷: make it a combo? Party voice never
  // sees this screen (no commerce), and flash/standby skip it entirely.
  if (tier === "custom" && !partyVoice && comboChoice === null) {
    return (
      <div style={{ minHeight: "100vh", background: BRAND.navy, fontFamily: "var(--jop-display)", color: "white" }}>
        <div role="main" style={{ maxWidth: "480px", margin: "0 auto", padding: "1.5rem 1.25rem" }}>
          <div style={{ textAlign: "center", marginBottom: "1.5rem", position: "relative" }}>
            <button onClick={() => setTier(null)} style={{
              position: "absolute", left: 0, top: 0,
              background: "none", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px",
              color: "rgba(255,255,255,0.6)", fontSize: "0.78rem", padding: "0.3rem 0.6rem", cursor: "pointer",
            }}>← Back</button>
            <img src="/jop_logo_wide.png" alt="The Joy of Pancakes" style={{ height: "96px", marginBottom: "0.5rem" }} />
            <div style={{ color: BRAND.gold, fontSize: "1.3rem", fontWeight: "bold" }}>Make it a combo?</div>
            <div style={{ color: BRAND.sky, fontSize: "0.8rem", marginTop: "0.25rem" }}>Take your pancake home forever</div>
          </div>

          {COMBO_ORDER.map((key) => {
            const combo = COMBOS[key];
            const priceCents = comboPriceCents(shift, key);
            const alaCents = comboAlaCarteCents(shift, key);
            const tint = key === "collector" ? "252,179,31" : "239,83,126"; // gold / pink
            const color = key === "collector" ? BRAND.gold : BRAND.pink;
            return (
              <button key={key} onClick={() => setComboChoice(key)} style={{
                width: "100%", boxSizing: "border-box", textAlign: "left", cursor: "pointer",
                background: `rgba(${tint},0.12)`, border: `2px solid rgba(${tint},0.6)`,
                borderRadius: "14px", padding: "1rem 1.1rem", marginBottom: "0.75rem",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "0.5rem" }}>
                  <span style={{ color, fontSize: "1.05rem", fontWeight: "bold", fontFamily: "var(--jop-display)" }}>{combo.emoji} {combo.label}</span>
                  <span style={{ color, fontSize: "1.15rem", fontWeight: "bold", fontFamily: "var(--jop-display)", whiteSpace: "nowrap" }}>
                    {fmtCents(priceCents)}
                    {alaCents > priceCents && (
                      <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.82rem", textDecoration: "line-through", marginLeft: "0.4rem" }}>{fmtCents(alaCents)}</span>
                    )}
                  </span>
                </div>
                <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "0.82rem", lineHeight: 1.5, marginTop: "0.3rem", fontFamily: "var(--jop-display)" }}>
                  {combo.pitch}
                </div>
              </button>
            );
          })}

          <button onClick={() => setComboChoice("none")} style={{
            width: "100%", boxSizing: "border-box", textAlign: "left", cursor: "pointer",
            background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: "12px", padding: "0.8rem 1.1rem",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "0.5rem" }}>
              <span style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.92rem", fontWeight: "bold", fontFamily: "var(--jop-display)" }}>📷 Just the pancake</span>
              <span style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.92rem", fontWeight: "bold", fontFamily: "var(--jop-display)", whiteSpace: "nowrap" }}>{customPrice}</span>
            </div>
          </button>

          <div style={{ textAlign: "center", marginTop: "1.5rem", color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
            Signed prints are stamped with your pancake's official GTS number
          </div>
        </div>
      </div>
    );
  }

  // ── JOIN FORM ──
  const isFreestyle = selectedDesign?.isFreestyle;
  const isPaidTier  = tier === "flash" || tier === "custom";
  const chosenCombo = tier === "custom" && COMBOS[comboChoice] ? COMBOS[comboChoice] : null;
  // Road-mode extras on offer for this paid order — an extra already inside
  // the chosen combo (Collector includes the signed print) never double-sells.
  const extrasOnOffer = isPaidTier && !partyVoice
    ? activeExtras(shift).filter((e) => !(chosenCombo?.addOns || []).includes(e.addOnType))
    : [];
  const extrasTotalCents = extrasOnOffer
    .filter((e) => extraChoices.includes(e.key))
    .reduce((sum, e) => sum + e.cents, 0);
  const baseTotalCents = tier === "flash" ? flashPriceCents(shift)
    : chosenCombo ? comboPriceCents(shift, comboChoice)
    : tier === "custom" ? customPriceCents(shift)
    : 0;
  const payTotal = fmtCents(baseTotalCents + extrasTotalCents);
  const tierHeading = partyVoice ? "🥞 Hop in line!"
    : tier === "flash" ? `⚡ Flash or Freestyle — ${flashPrice}`
    : chosenCombo ? `${chosenCombo.emoji} ${chosenCombo.label} — ${fmtCents(comboPriceCents(shift, comboChoice))}`
    : tier === "custom" ? `📷 Your Own Photo — ${customPrice}`
    : "🕐 Standby — Free";

  function toggleExtra(key) {
    setExtraChoices((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  }

  function toggleColor(colorId) {
    setSelectedColors(prev =>
      prev.includes(colorId) ? prev.filter(c => c !== colorId) : prev.length < 3 ? [...prev, colorId] : prev
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: BRAND.navy, fontFamily: "var(--jop-display)", color: "white" }}>
      <div role="main" style={{ maxWidth: "480px", margin: "0 auto", padding: "1.5rem 1.25rem" }}>

        {/* Header — no back button in party voice (there's no picker behind it) */}
        <div style={{ textAlign: "center", marginBottom: "1.25rem", position: "relative" }}>
          {!partyVoice && (
            <button onClick={() => { if (!submitting) { if (tier === "custom") setComboChoice(null); else setTier(null); } }} style={{
              position: "absolute", left: 0, top: 0,
              background: "none", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px",
              color: "rgba(255,255,255,0.6)", fontSize: "0.78rem", padding: "0.3rem 0.6rem", cursor: "pointer",
            }}>← Back</button>
          )}
          <img src="/jop_logo_wide.png" alt="The Joy of Pancakes" style={{ height: "84px", marginBottom: "0.4rem" }} />
          <div style={{ color: BRAND.gold, fontSize: "1.15rem", fontWeight: "bold" }}>{tierHeading}</div>
          {guestSafeLabel(shift) && <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", marginTop: "0.25rem" }}>{guestSafeLabel(shift)}</div>}
          {partyVoice && (
            <div style={{ color: BRAND.sky, fontSize: "0.85rem", marginTop: "0.25rem" }}>
              Pick a design or share a photo — I'll paint yours at the griddle.
            </div>
          )}
        </div>

        {/* Name */}
        <div style={{ marginBottom: "1rem" }}>
          <label style={{ color: BRAND.cream, fontSize: "0.85rem", fontWeight: "bold", display: "block", marginBottom: "0.4rem" }}>Your name *</label>
          <input
            type="text" value={name} onChange={e => setName(e.target.value)}
            placeholder="What should we call you?"
            style={{ width: "100%", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(74,164,225,0.3)", borderRadius: "10px", color: "white", fontSize: "1rem", padding: "0.75rem", outline: "none", boxSizing: "border-box" }}
          />
        </div>

        {/* Email */}
        <div style={{ marginBottom: "1.25rem" }}>
          <label style={{ color: BRAND.cream, fontSize: "0.85rem", display: "block", marginBottom: "0.4rem" }}>Email (optional — we'll send your keepsake)</label>
          <input
            type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="your@email.com"
            style={{ width: "100%", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(74,164,225,0.3)", borderRadius: "10px", color: "white", fontSize: "1rem", padding: "0.75rem", outline: "none", boxSizing: "border-box" }}
          />
        </div>

        {/* Mode toggle — standby/party only; paid tiers lock the mode at pick time */}
        {effectiveTier === "standby" && (
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
          <button onClick={() => { setMode("design"); setPhotoFile(null); setCroppedBlob(null); }}
            style={{ flex: 1, padding: "0.6rem", borderRadius: "10px", fontWeight: "bold", fontSize: "0.85rem", cursor: "pointer",
              background: mode === "design" ? "rgba(252,179,31,0.15)" : "rgba(255,255,255,0.05)",
              border: mode === "design" ? "2px solid rgba(252,179,31,0.6)" : "2px solid rgba(255,255,255,0.1)",
              color: mode === "design" ? BRAND.gold : "rgba(255,255,255,0.5)",
          }}>⚡ Pick a design</button>
          <button onClick={() => { setMode("photo"); setSelectedDesign(null); }}
            style={{ flex: 1, padding: "0.6rem", borderRadius: "10px", fontWeight: "bold", fontSize: "0.85rem", cursor: "pointer",
              background: mode === "photo" ? "rgba(74,164,225,0.15)" : "rgba(255,255,255,0.05)",
              border: mode === "photo" ? "2px solid rgba(74,164,225,0.6)" : "2px solid rgba(255,255,255,0.1)",
              color: mode === "photo" ? BRAND.sky : "rgba(255,255,255,0.5)",
          }}>📷 Upload a photo</button>
        </div>
        )}

        {mode === "design" ? (
          <>
            {/* Design picker */}
            <div style={{ marginBottom: "1.25rem" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.6rem" }}>
                {allDesigns.map(design => (
                  <button
                    key={design.id}
                    onClick={() => { setSelectedDesign(design); setFreestyleText(""); }}
                    style={{
                      background: selectedDesign?.id === design.id ? "rgba(252,179,31,0.15)" : "rgba(255,255,255,0.05)",
                      border: selectedDesign?.id === design.id ? "2px solid rgba(252,179,31,0.7)" : "2px solid rgba(255,255,255,0.1)",
                      borderRadius: "12px", padding: "0.5rem", cursor: "pointer",
                      display: "flex", flexDirection: "column", alignItems: "center", gap: "0.3rem",
                      transition: "all 0.15s ease",
                    }}
                  >
                    {selectedDesign?.id === design.id && design.quote && (() => {
                      const isSplit = design.quote.includes("|");
                      const parts = isSplit ? design.quote.split("|") : null;
                      return isSplit ? (
                        <div style={{ display: "flex", gap: "3px", marginBottom: "4px" }}>
                          <style>{`@keyframes gtsBubbleGrow { 0% { opacity:0; transform:scale(0.4); } 60% { opacity:1; transform:scale(1.05); } 100% { opacity:1; transform:scale(1); } }`}</style>
                          {parts.map((q, qi) => (
                            <div key={qi} style={{ background: "#ffdaad", borderRadius: "8px", padding: "0.3rem 0.4rem", position: "relative", boxShadow: "0 2px 5px rgba(0,0,0,0.2)", animation: `gtsBubbleGrow 0.35s ${qi * 0.12}s cubic-bezier(0.34,1.4,0.64,1) both`, flex: 1 }}>
                              <div style={{ color: "#1d52a0", fontSize: "0.55rem", fontStyle: "italic", fontWeight: "600", textAlign: "center", lineHeight: 1.25 }}>"{q}"</div>
                              <div style={{ position: "absolute", bottom: "-4px", left: qi === 0 ? "60%" : "40%", transform: "translateX(-50%)", width: 0, height: 0, borderLeft: "4px solid transparent", borderRight: "4px solid transparent", borderTop: "4px solid #ffdaad" }} />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ background: "#ffdaad", borderRadius: "8px", padding: "0.35rem 0.5rem", marginBottom: "4px", position: "relative", boxShadow: "0 2px 5px rgba(0,0,0,0.2)", animation: "gtsBubbleGrow 0.35s cubic-bezier(0.34,1.4,0.64,1) both" }}>
                          <style>{`@keyframes gtsBubbleGrow { 0% { opacity:0; transform:scale(0.4); } 60% { opacity:1; transform:scale(1.05); } 100% { opacity:1; transform:scale(1); } }`}</style>
                          <div style={{ color: "#1d52a0", fontSize: "0.58rem", fontStyle: "italic", fontWeight: "600", textAlign: "center", lineHeight: 1.3 }}>"{design.quote}"</div>
                          <div style={{ position: "absolute", bottom: "-5px", left: "50%", transform: "translateX(-50%)", width: 0, height: 0, borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: "5px solid #ffdaad" }} />
                        </div>
                      );
                    })()}
                    <div style={{ width: "64px", height: "64px", borderRadius: "8px", overflow: "hidden", background: "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <img src={design.imageUrl} alt={design.label} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                    </div>
                    <div style={{ color: selectedDesign?.id === design.id ? BRAND.gold : "rgba(255,255,255,0.5)", fontSize: "0.7rem", fontWeight: "bold", textAlign: "center" }}>{design.label}</div>
                    {selectedDesign?.id === design.id && design.subtitle && (
                      <div style={{ color: "rgba(255,218,173,0.6)", fontSize: "0.58rem", fontStyle: "italic", lineHeight: 1.3, paddingRight: "0.15em", textAlign: "center" }}>{design.subtitle}</div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Freestyle text */}
            {isFreestyle && (
              <div style={{ marginBottom: "1.25rem" }}>
                <label style={{ color: BRAND.cream, fontSize: "0.85rem", display: "block", marginBottom: "0.4rem" }}>What should we draw?</label>
                <input
                  type="text" value={freestyleText} onChange={e => setFreestyleText(e.target.value)}
                  placeholder="Describe it in a few words"
                  style={{ width: "100%", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(74,164,225,0.3)", borderRadius: "10px", color: "white", fontSize: "1rem", padding: "0.75rem", outline: "none", boxSizing: "border-box" }}
                />
              </div>
            )}
          </>
        ) : (
          /* Photo upload */
          <div style={{ marginBottom: "1.25rem" }}>
            {!photoFile ? (
              <>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={e => { if (e.target.files?.[0]) { setPhotoFile(e.target.files[0]); setFramingDone(false); setCroppedBlob(null); } }} style={{ display: "none" }} />
                <button onClick={() => fileInputRef.current?.click()} style={{
                  width: "100%", padding: "2rem", borderRadius: "12px",
                  background: "rgba(74,164,225,0.08)", border: "2px dashed rgba(74,164,225,0.4)",
                  color: BRAND.sky, fontSize: "1rem", fontWeight: "bold", cursor: "pointer",
                  display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem",
                }}>
                  <span style={{ fontSize: "2rem" }}>📷</span>
                  Tap to choose a photo
                </button>
              </>
            ) : !framingDone ? (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                  <label style={{ color: BRAND.cream, fontSize: "0.85rem", fontWeight: "bold" }}>Fill the circle with your subject</label>
                  <button onClick={() => { setPhotoFile(null); setCroppedBlob(null); setFramingDone(false); }} style={{ background: "none", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "6px", color: "rgba(255,255,255,0.5)", fontSize: "0.75rem", padding: "0.2rem 0.5rem", cursor: "pointer" }}>← Different photo</button>
                </div>
                <FramingTool imageFile={photoFile} onCropped={setCroppedBlob} />
                <button onClick={() => setFramingDone(true)} disabled={!croppedBlob} style={{
                  width: "100%", marginTop: "0.75rem", padding: "0.75rem", borderRadius: "10px", border: "none",
                  background: croppedBlob ? BRAND.gold : "rgba(255,255,255,0.1)",
                  color: croppedBlob ? BRAND.dark : "rgba(255,255,255,0.3)",
                  fontSize: "0.95rem", fontWeight: "bold", cursor: croppedBlob ? "pointer" : "not-allowed",
                }}>Looks good ✓</button>
              </div>
            ) : (
              <div style={{ textAlign: "center" }}>
                <div style={{ display: "inline-block", borderRadius: "50%", overflow: "hidden", width: "160px", height: "160px", border: `3px solid ${BRAND.gold}`, margin: "0 auto 0.75rem" }}>
                  <img src={URL.createObjectURL(croppedBlob)} alt="Framed" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
                <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center" }}>
                  <button onClick={() => setFramingDone(false)} style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "8px", color: "rgba(255,255,255,0.6)", fontSize: "0.82rem", padding: "0.45rem 0.75rem", cursor: "pointer" }}>Re-frame</button>
                  <button onClick={() => { setPhotoFile(null); setCroppedBlob(null); setFramingDone(false); }} style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "8px", color: "rgba(255,255,255,0.6)", fontSize: "0.82rem", padding: "0.45rem 0.75rem", cursor: "pointer" }}>Different photo</button>
                </div>
                <div style={{ color: "#70be44", fontSize: "0.82rem", marginTop: "0.5rem", fontWeight: "bold" }}>✓ Looking good!</div>
              </div>
            )}

            {/* Pancake title — the pancake's public name (gallery, keepsake).
                Same field as CustomerOrderView's "Title / Subject"; skipping it
                gets the piece an "Untitled #N" number instead of a bare label. */}
            <div style={{ marginTop: "1rem" }}>
              <label style={{ color: BRAND.cream, fontSize: "0.85rem", display: "block", marginBottom: "0.4rem" }}>Name your pancake <span style={{ color: "rgba(255,255,255,0.4)", fontWeight: "normal" }}>(optional)</span></label>
              <input
                type="text" value={subjectName} onChange={e => setSubjectName(e.target.value)}
                placeholder="What is this a picture of?"
                maxLength={60}
                style={{ width: "100%", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(74,164,225,0.3)", borderRadius: "10px", color: "white", fontSize: "1rem", padding: "0.75rem", outline: "none", boxSizing: "border-box" }}
              />
              <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.75rem", marginTop: "0.3rem", lineHeight: 1.4 }}>
                This becomes the piece's name in the gallery. Skip it and we'll number it — like "Untitled #45".
              </div>
            </div>

            {/* Artist notes — same field the token flow captures */}
            <div style={{ marginTop: "0.85rem" }}>
              <label style={{ color: BRAND.cream, fontSize: "0.85rem", display: "block", marginBottom: "0.4rem" }}>A note for the artist <span style={{ color: "rgba(255,255,255,0.4)", fontWeight: "normal" }}>(optional)</span></label>
              <input
                type="text" value={photoNotes} onChange={e => setPhotoNotes(e.target.value)}
                placeholder="a suggestion, a bit of inspiration…"
                maxLength={120}
                style={{ width: "100%", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(74,164,225,0.3)", borderRadius: "10px", color: "white", fontSize: "1rem", padding: "0.75rem", outline: "none", boxSizing: "border-box" }}
              />
            </div>
          </div>
        )}

        {/* Color picker */}
        <div style={{ marginBottom: "1.5rem" }}>
          <label style={{ color: BRAND.cream, fontSize: "0.85rem", display: "block", marginBottom: "0.5rem" }}>Favorite colors (up to 3)</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            {COLOR_OPTIONS.map(c => (
              <button
                key={c.id}
                onClick={() => toggleColor(c.id)}
                style={{
                  width: "38px", height: "38px", borderRadius: "50%", cursor: "pointer",
                  background: c.hex, border: selectedColors.includes(c.id) ? "3px solid white" : "2px solid rgba(0,0,0,0.5)",
                  boxShadow: selectedColors.includes(c.id) ? "0 0 8px rgba(255,255,255,0.4)" : "none",
                  transform: selectedColors.includes(c.id) ? "scale(1.15)" : "scale(1)",
                  transition: "all 0.15s ease",
                }}
                title={c.label}
              />
            ))}
          </div>
        </div>

        {/* Road-mode extras — one-tap add-ons priced by the shift */}
        {extrasOnOffer.length > 0 && (
          <div style={{ marginBottom: "1.5rem" }}>
            <label style={{ color: BRAND.cream, fontSize: "0.85rem", fontWeight: "bold", display: "block", marginBottom: "0.5rem" }}>Make it extra</label>
            {extrasOnOffer.map((e) => {
              const on = extraChoices.includes(e.key);
              return (
                <button key={e.key} onClick={() => toggleExtra(e.key)} style={{
                  width: "100%", boxSizing: "border-box", textAlign: "left", cursor: "pointer",
                  background: on ? "rgba(239,83,126,0.15)" : "rgba(255,255,255,0.04)",
                  border: on ? "2px solid rgba(239,83,126,0.65)" : "2px solid rgba(255,255,255,0.12)",
                  borderRadius: "12px", padding: "0.7rem 0.9rem", marginBottom: "0.5rem",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "0.5rem" }}>
                    <span style={{ color: on ? BRAND.pink : "rgba(255,255,255,0.75)", fontSize: "0.95rem", fontWeight: "bold", fontFamily: "var(--jop-display)" }}>
                      {on ? "✓ " : ""}{e.emoji} {e.label}
                    </span>
                    <span style={{ color: on ? BRAND.pink : "rgba(255,255,255,0.55)", fontSize: "0.95rem", fontWeight: "bold", fontFamily: "var(--jop-display)", whiteSpace: "nowrap" }}>+{fmtCents(e.cents)}</span>
                  </div>
                  {e.note && (
                    <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.75rem", lineHeight: 1.45, marginTop: "0.25rem", fontFamily: "var(--jop-display)" }}>{e.note}</div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ background: "rgba(239,83,126,0.15)", border: "1px solid rgba(239,83,126,0.4)", borderRadius: "8px", padding: "0.6rem 0.75rem", marginBottom: "1rem", color: BRAND.pink, fontSize: "0.85rem" }}>
            {error}
          </div>
        )}

        {/* Disclaimer */}
        <div style={{ marginBottom: "1rem" }}>
          <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.82rem", lineHeight: 1.5, margin: "0 0 0.5rem" }}>
            We reserve the right to reject any request for being hateful or inappropriate. Your artist may adjust the framing of your image to get the best pancake result.
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <input
              type="checkbox" id="join-disclaimer"
              checked={disclaimerChecked}
              onChange={e => setDisclaimerChecked(e.target.checked)}
              style={{ width: "20px", height: "20px", flexShrink: 0, accentColor: BRAND.sky }}
            />
            <label htmlFor="join-disclaimer" style={{ color: BRAND.cream, fontSize: "0.85rem", cursor: "pointer" }}>I understand.</label>
          </div>
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={!name.trim() || (mode === "design" ? !selectedDesign : !framingDone) || !disclaimerChecked || submitting}
          style={{
            width: "100%", padding: "1rem", borderRadius: "12px", border: "none",
            background: (name.trim() && (mode === "design" ? selectedDesign : framingDone) && disclaimerChecked && !submitting) ? BRAND.pink : "rgba(255,255,255,0.1)",
            color: (name.trim() && (mode === "design" ? selectedDesign : framingDone) && disclaimerChecked && !submitting) ? "white" : "rgba(255,255,255,0.3)",
            fontSize: "1.1rem", fontWeight: "bold", cursor: (name.trim() && (mode === "design" ? selectedDesign : framingDone) && disclaimerChecked) ? "pointer" : "not-allowed",
          }}
        >
          {submitting
            ? (isPaidTier ? "Heading to checkout..." : "Submitting...")
            : (partyVoice ? "Hop in Line 🥞"
              : isPaidTier ? `Continue to Payment — ${payTotal}`
              : "Submit Standby Request 🥞")}
        </button>
        <p style={{ color: BRAND.cream, fontSize: "0.75rem", textAlign: "center", margin: "0.5rem 0 0", lineHeight: 1.5 }}>
          {partyVoice
            ? "We'll call your name when it's your turn — keep an eye on the screen!"
            : isPaidTier
            ? "Secure checkout with Square. If you don't finish paying, your request is saved as a free standby — you can upgrade anytime from your order page."
            : `Free — we get to standby requests when there's breathing room, no promises. You can upgrade to a guaranteed spot anytime from your order page (${mode === "photo" ? `${customPrice} for a custom photo pancake` : `${flashPrice} for a flash design`}).`}
        </p>

        <div style={{ textAlign: "center", marginTop: "1.5rem", color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
          thejoyofpancakes.com
        </div>
      </div>
    </div>
  );
}
